import { Pencil, Plus, X } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { useInvalidate } from "../misc/useInvalidate";
import type {
  Deal,
  DealLine,
  DealLineInterval,
  MarkDealWonLine,
  Product,
} from "../types";
import { DEAL_LINE_INTERVALS, dealLineTotals } from "./dealLines";
import { isDealOpen } from "./dealStatus";

// Produktlinjer på muligheten (kap. 9.2 og 9.4): produkt fra katalogen,
// beskrivelse, antall, pris og periode. Brukes i Vunnet-dialogen og i
// detaljvisningen. Linjene bestemmer engangs- og månedsverdien; databasen
// (og demodataene) regner verdiene på muligheten på nytt når linjene endres.

export type LineDraft = {
  key: string;
  id?: Identifier;
  product_id: Identifier | null;
  description: string;
  // Tekst mens vi skriver, så «1 490,50» og tomt felt går an.
  quantity: string;
  unit_price: string;
  interval: DealLineInterval;
};

const NO_PRODUCTS: Product[] = [];
const NO_LINES: DealLine[] = [];

let keySeq = 0;
const nextKey = () => `line-${++keySeq}`;

// «1 490,50» → 1490.5. Tomt eller ugyldig gir 0.
export const parseAmount = (value: string) => {
  const number = Number(value.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(number) ? number : 0;
};

// «85 000», «1 490,50» – samme format som format_nok i databasen.
export const formatKr = (value: number) => {
  const rounded = Math.round(value * 100) / 100;
  const [int, dec] = Math.abs(rounded).toFixed(2).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (rounded < 0 ? "-" : "") + grouped + (dec === "00" ? "" : `,${dec}`);
};

const numberText = (value: number | null | undefined) =>
  value == null ? "" : String(value).replace(".", ",");

export const lineDraftFromDealLine = (line: DealLine): LineDraft => ({
  key: nextKey(),
  id: line.id,
  product_id: line.product_id,
  description: line.description ?? "",
  quantity: numberText(Number(line.quantity ?? 1)),
  unit_price: numberText(Number(line.unit_price ?? 0)),
  interval: line.interval ?? "one_time",
});

export const lineDraftFromProduct = (product?: Product | null): LineDraft => ({
  key: nextKey(),
  product_id: product?.id ?? null,
  description: product?.name ?? "",
  quantity: "1",
  unit_price: numberText(product ? Number(product.sales_price ?? 0) : 0),
  interval: product?.billing_interval ?? "one_time",
});

export const lineDraftAmounts = (lines: LineDraft[]) =>
  lines.map((line) => ({
    quantity: parseAmount(line.quantity),
    unit_price: parseAmount(line.unit_price),
    interval: line.interval,
  }));

export const lineDraftTotals = (lines: LineDraft[]) =>
  dealLineTotals(lineDraftAmounts(lines));

export const toWonLineInput = (lines: LineDraft[]): MarkDealWonLine[] =>
  lines
    .filter((line) => line.product_id != null)
    .map((line) => ({
      product_id: line.product_id!,
      description: line.description.trim() || null,
      quantity: parseAmount(line.quantity),
      unit_price: parseAmount(line.unit_price),
      interval: line.interval,
    }));

// Hele katalogen: aktive produkter kan velges, og et inaktivt produkt som
// allerede står på en linje, vises fortsatt.
export const useCatalogProducts = () => {
  const { data = NO_PRODUCTS, isPending } = useGetList<Product>(
    "products",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
      filter: {},
    },
    { staleTime: 30_000 },
  );
  return { products: data, isPending };
};

export const useIntervalLabel = () => {
  const translate = useTranslate();
  return (interval: DealLineInterval) =>
    translate(`crm.deals.lines.intervals.${interval}`, {
      _: {
        one_time: "engangs",
        monthly: "månedlig",
        quarterly: "kvartalsvis",
        yearly: "årlig",
      }[interval],
    });
};

const selectClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-xs dark:bg-input/30 aria-invalid:border-destructive";

/**
 * Redigerer produktlinjene. Kontrollert: `lines` inn, `onChange` ut. Å velge
 * produkt fyller beskrivelse, pris og periode fra katalogen.
 */
export const DealLinesEditor = ({
  lines,
  onChange,
  products,
  showMissingProduct = false,
}: {
  lines: LineDraft[];
  onChange: (lines: LineDraft[]) => void;
  products: Product[];
  // Marker linjer uten produkt (etter et forsøk på å lagre).
  showMissingProduct?: boolean;
}) => {
  const translate = useTranslate();
  const intervalLabel = useIntervalLabel();

  const patch = (key: string, change: Partial<LineDraft>) =>
    onChange(
      lines.map((line) => (line.key === key ? { ...line, ...change } : line)),
    );

  const chooseProduct = (key: string, productId: string) => {
    const product = products.find((p) => String(p.id) === productId);
    if (!product) {
      patch(key, { product_id: null });
      return;
    }
    const from = lineDraftFromProduct(product);
    patch(key, {
      product_id: product.id,
      description: from.description,
      unit_price: from.unit_price,
      interval: from.interval,
    });
  };

  const options = (selected: Identifier | null) =>
    products.filter(
      (p) =>
        p.is_active !== false ||
        (selected != null && String(p.id) === String(selected)),
    );

  return (
    <div className="flex flex-col gap-2">
      {lines.length > 0 && (
        <div className="hidden grid-cols-[minmax(0,1.3fr)_minmax(0,1.5fr)_4rem_6.5rem_7.5rem_2rem] gap-2 text-xs text-muted-foreground sm:grid">
          <span>{translate("crm.deals.lines.product", { _: "Produkt" })}</span>
          <span>
            {translate("crm.deals.lines.description", { _: "Beskrivelse" })}
          </span>
          <span>{translate("crm.deals.lines.quantity", { _: "Antall" })}</span>
          <span>{translate("crm.deals.lines.price", { _: "Pris" })}</span>
          <span>{translate("crm.deals.lines.interval", { _: "Periode" })}</span>
          <span />
        </div>
      )}
      {lines.map((line, index) => {
        const n = index + 1;
        const label = (key: string, fallback: string) =>
          translate("crm.deals.lines.row_label", {
            _: "%{field} (linje %{n})",
            field: translate(`crm.deals.lines.${key}`, { _: fallback }),
            n,
          });
        const missing = showMissingProduct && line.product_id == null;
        return (
          <div
            key={line.key}
            className="grid grid-cols-2 gap-2 rounded-md border p-2 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1.5fr)_4rem_6.5rem_7.5rem_2rem] sm:border-0 sm:p-0"
          >
            <select
              className={cn(selectClass, "col-span-2 sm:col-span-1")}
              aria-label={label("product", "Produkt")}
              aria-invalid={missing || undefined}
              value={line.product_id == null ? "" : String(line.product_id)}
              onChange={(e) => chooseProduct(line.key, e.target.value)}
            >
              <option value="">
                {translate("crm.deals.lines.choose_product", {
                  _: "Velg produkt …",
                })}
              </option>
              {options(line.product_id).map((product) => (
                <option key={product.id} value={String(product.id)}>
                  {product.name}
                </option>
              ))}
            </select>
            <Input
              className="col-span-2 h-9 sm:col-span-1"
              aria-label={label("description", "Beskrivelse")}
              value={line.description}
              onChange={(e) => patch(line.key, { description: e.target.value })}
            />
            <Input
              className="h-9"
              inputMode="decimal"
              aria-label={label("quantity", "Antall")}
              value={line.quantity}
              onChange={(e) => patch(line.key, { quantity: e.target.value })}
            />
            <Input
              className="h-9"
              inputMode="decimal"
              aria-label={label("price", "Pris")}
              value={line.unit_price}
              onChange={(e) => patch(line.key, { unit_price: e.target.value })}
            />
            <select
              className={selectClass}
              aria-label={label("interval", "Periode")}
              value={line.interval}
              onChange={(e) =>
                patch(line.key, {
                  interval: e.target.value as DealLineInterval,
                })
              }
            >
              {DEAL_LINE_INTERVALS.map((interval) => (
                <option key={interval} value={interval}>
                  {intervalLabel(interval)}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9"
              aria-label={translate("crm.deals.lines.remove", {
                _: "Fjern linje %{n}",
                n,
              })}
              onClick={() =>
                onChange(lines.filter((other) => other.key !== line.key))
              }
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => onChange([...lines, lineDraftFromProduct(null)])}
      >
        <Plus className="h-4 w-4" />
        {translate("crm.deals.lines.add", { _: "Legg til produkt" })}
      </Button>
    </div>
  );
};

const sameLine = (draft: LineDraft, line: DealLine, position: number) =>
  String(draft.product_id ?? "") === String(line.product_id ?? "") &&
  draft.description === (line.description ?? "") &&
  parseAmount(draft.quantity) === Number(line.quantity) &&
  parseAmount(draft.unit_price) === Number(line.unit_price) &&
  draft.interval === line.interval &&
  position === line.position;

/**
 * Produktlinjene i detaljvisningen: en kort liste, og «Rediger» på åpne
 * muligheter. Lagring sletter, endrer og legger til linjer i deal_lines.
 */
export const DealLinesPanel = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const invalidate = useInvalidate();
  const intervalLabel = useIntervalLabel();
  const { products } = useCatalogProducts();
  const { data: lines = NO_LINES, isPending } = useGetList<DealLine>(
    "deal_lines",
    {
      filter: { deal_id: deal.id },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "position", order: "ASC" },
    },
  );
  const [editing, setEditing] = useState<LineDraft[] | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const open = isDealOpen(deal) && !deal.archived_at;
  const sorted = useMemo(
    () => [...lines].sort((a, b) => a.position - b.position),
    [lines],
  );

  if (!open && sorted.length === 0) return null;

  const save = async () => {
    if (!editing) return;
    if (editing.some((line) => line.product_id == null)) {
      setAttempted(true);
      return;
    }
    setSaving(true);
    try {
      const keep = new Set(
        editing.filter((l) => l.id != null).map((l) => String(l.id)),
      );
      for (const line of sorted) {
        if (keep.has(String(line.id))) continue;
        await dataProvider.delete<DealLine>("deal_lines", {
          id: line.id,
          previousData: line,
        });
      }
      for (const [index, draft] of editing.entries()) {
        const data = {
          deal_id: deal.id,
          product_id: draft.product_id,
          description: draft.description.trim(),
          quantity: parseAmount(draft.quantity),
          unit_price: parseAmount(draft.unit_price),
          interval: draft.interval,
          position: index + 1,
        };
        const existing = sorted.find((l) => String(l.id) === String(draft.id));
        if (existing) {
          if (sameLine(draft, existing, index + 1)) continue;
          await dataProvider.update<DealLine>("deal_lines", {
            id: existing.id,
            data,
            previousData: existing,
          });
        } else {
          await dataProvider.create<DealLine>("deal_lines", {
            data: data as Partial<DealLine>,
          });
        }
      }
      setEditing(null);
      setAttempted(false);
      notify(
        translate("crm.deals.lines.saved", { _: "Produktlinjene er lagret" }),
      );
    } catch (error) {
      notify((error as Error)?.message || "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setSaving(false);
      invalidate("deal_lines", "deals");
    }
  };

  const productName = (id: Identifier | null) =>
    products.find((p) => String(p.id) === String(id))?.name;

  return (
    <section
      className="m-4 flex flex-col gap-2"
      aria-label={translate("crm.deals.lines.title", { _: "Produktlinjer" })}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs tracking-wide text-muted-foreground">
          {translate("crm.deals.lines.title", { _: "Produktlinjer" })}
        </h3>
        {/* Ikke før linjene er hentet: redigering starter fra dem. */}
        {open && !editing && !isPending && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7"
            onClick={() => setEditing(sorted.map(lineDraftFromDealLine))}
          >
            <Pencil className="h-3.5 w-3.5" />
            {translate("crm.deals.lines.edit", { _: "Rediger linjer" })}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <DealLinesEditor
            lines={editing}
            onChange={setEditing}
            products={products}
            showMissingProduct={attempted}
          />
          {attempted && editing.some((line) => line.product_id == null) && (
            <p className="text-xs text-destructive">
              {translate("crm.deals.lines.product_required", {
                _: "Velg et produkt fra produktkatalogen på hver linje.",
              })}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => {
                setEditing(null);
                setAttempted(false);
              }}
            >
              {translate("ra.action.cancel", { _: "Avbryt" })}
            </Button>
            <Button type="button" size="sm" disabled={saving} onClick={save}>
              {translate("crm.deals.lines.save", { _: "Lagre linjer" })}
            </Button>
          </div>
        </div>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.deals.lines.empty", {
            _: "Ingen produktlinjer ennå.",
          })}
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5 text-sm">
          {sorted.map((line) => (
            <li key={line.id} className="flex gap-2">
              <span className="min-w-0 flex-1 truncate">
                {line.description || productName(line.product_id) || "—"}
                {Number(line.quantity) !== 1 &&
                  ` × ${formatKr(Number(line.quantity))}`}
              </span>
              <span className="shrink-0 text-muted-foreground">
                {intervalLabel(line.interval)}
              </span>
              <span className="w-28 shrink-0 text-right tabular-nums">
                {formatKr(Number(line.quantity) * Number(line.unit_price))} kr
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
